import { defineStore } from 'pinia';
import { getServicePriceForDate } from 'src/utils/servicePrice';
import { Notify } from 'quasar';
import type {
  ReservationServiceSchema,
  ReservationUserSchema,
  TimeSlotOverviewSchema,
  TimeSlotSchema,
  VisitReservationSchema,
  ReservationCompanySchema,
  ServiceGroupSchema,
  CreateVisitReservationTimeSlotSchema,
  DiagnosticAnswerResponse,
  DiagnosticSchema,
  CreateVisitReservationSchema,
  CreateSubstituteReservationSchema,
  CreateWaitingPatientReservationSchema,
  GroupVisitSubstituteSchema,
  LocationSchema,
  ReceiptPatientPaymentType,
  ReceiptPaymentType,
} from 'src/api';
import { DiagnosticsApi, PublicApi } from 'src/api';
import { ReservationsApi } from 'src/api';
import type { Errors } from 'src/utils/apiErrors';
import gettext, { currentLanguage } from 'src/utils/gettext';
import { useUser } from 'stores/user/useUser';
import { useParsePhoneNumberByPrefix } from 'src/composables/useParsePhoneNumberByPrefix';
import { buildSubdomainUrl, getBaseDomain } from 'src/utils/domainUtils';

const { $gettext } = gettext;

export interface PatientDetails {
  patientFirstName: string;
  patientLastName: string;
  patientEmail: string;
  patientPhone: string;
  patientNote: string;
  patientFileInReservation: File | null;
  gdprConsent: boolean;
  receiveNewsletterConsent: boolean;
  vopConsent: boolean;
}

export interface FormNewSubstituteReservation {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phone: string;
  note: string;
  file: File | null;
  gdprConsent: boolean;
  receiveNewsletterConsent: boolean;
  vopConsent: boolean;
}

export interface FormNewReservation {
  firstName: string;
  lastName: string;
  email: string;
  countryCode: string;
  phone: string;
  note: string;
  file: File | null;
  gdprConsent: boolean;
  receiveNewsletterConsent: boolean;
  vopConsent: boolean;
  paymentType: ReceiptPatientPaymentType | ReceiptPaymentType | null;
}

export interface ReservationSymptomsParams {
  patientsBirthNumber: string;
  patientDiagnosisId: string;
  additionalOption: string;
}

interface ReservationState {
  isFetching: boolean;
  companyId: string | null;
  company: ReservationCompanySchema | null;
  user: ReservationUserSchema | null;
  service: ReservationServiceSchema | null;
  location: LocationSchema | null;
  appointmentsSlotsSelected: CreateVisitReservationTimeSlotSchema[];
  showAppointmentsFromDate: string | null;
  visitStartTime: Date | null;
  patientData: PatientDetails | null;
  reservation: VisitReservationSchema | null;
  companyUsers: ReservationUserSchema[];
  companyServices: ReservationServiceSchema[];
  companyServiceGroups: ServiceGroupSchema[];
  companyTimeSlots: TimeSlotSchema[];
  companyAvailableDays: TimeSlotOverviewSchema[];
  slotsLoading: boolean;
  anyAvailableUser: boolean;
  errors: Errors;
  formNewReservation: FormNewReservation | null;
  formNewSubstituteReservation: FormNewSubstituteReservation | null;
  paymentBefore: boolean | null;
  diagnosticQuestionareOptions: DiagnosticAnswerResponse[];
  diagnosesInCompany: DiagnosticSchema[];
  reservationSymptomsParams: ReservationSymptomsParams;
  payWithTicket: boolean;
  canPayWithTicket: boolean;
  selectedSubstituteSlot: TimeSlotSchema | null;
  substituteReservation: GroupVisitSubstituteSchema | null;
}

const ERROR_MESSAGES = {
  gdpr_consent: $gettext(
    'Pro dokončení rezervace je nutné souhlasit se zásadami zpracování osobních údajů.'
  ),
  patient_phone: $gettext('Zadané telefonní číslo je neplatné.'),
  default: $gettext('Rezervace nemohla být vytvořena'),
};

let _pendingFetchCompany: Promise<void> | null = null;

export const useReservations = defineStore({
  id: 'useReservations',
  state: (): ReservationState => {
    return {
      isFetching: false,
      companyId: null,
      service: null,
      appointmentsSlotsSelected: [],
      showAppointmentsFromDate: null,
      user: null,
      visitStartTime: null,
      location: null,
      company: null,
      patientData: null,
      reservation: null,
      companyUsers: [],
      companyServiceGroups: [],
      companyServices: [],
      companyTimeSlots: [],
      companyAvailableDays: [],
      slotsLoading: false,
      anyAvailableUser: false,
      errors: {},
      formNewReservation: null,
      formNewSubstituteReservation: null,
      paymentBefore: null,
      diagnosticQuestionareOptions: [],
      diagnosesInCompany: [],
      reservationSymptomsParams: {
        patientsBirthNumber: '',
        patientDiagnosisId: '',
        additionalOption: '',
      },
      payWithTicket: false,
      canPayWithTicket: false,
      selectedSubstituteSlot: null,
      substituteReservation: null,
    };
  },
  getters: {
    hasFourthStepAccess(): boolean {
      return !!this.reservation;
    },
    hasThirdStepAccess(): boolean {
      return (
        !!this.service &&
        !!this.user &&
        this.appointmentsSlotsSelected.length > 0
      );
    },
    hasSecondStepAccess(): boolean {
      return !!this.service;
    },
    getReservationId(): string {
      if (!this.reservation?.id) {
        console.debug('Reservation ID is not set');
        return '';
      }
      return this.reservation.id;
    },
    getCompanyId(): string {
      if (!this.companyId) {
        console.debug('Company ID is not set');
        return '';
      }
      return this.companyId;
    },
    getPatientsNin(): string | undefined {
      return this.reservationSymptomsParams.patientsBirthNumber || undefined;
    },
    getVisitId(): string {
      if (!this.reservation?.id) {
        console.debug('Visit ID is not set');
        return '';
      }
      return this.reservation.id;
    },
    getVisitStartTime(): Date {
      if (!this.visitStartTime) {
        console.debug('Visit start time is not set');
        return new Date(0);
      }
      return this.visitStartTime;
    },
    getServiceId(): string {
      if (!this.service?.id) {
        console.debug('Service ID is not set');
        return '';
      }
      return this.service.id;
    },
    getUserId(): string {
      if (!this.user?.id) {
        console.debug('User ID is not set');
        return '';
      }
      return this.user.id;
    },
    getUserIds(): string[] {
      if (this.user) {
        return [this.user.id ?? ''];
      }
      return this.companyUsers.map((user) => user.id ?? '');
    },
    // price of one visit, for the first selected slot (or today when none is selected yet)
    getServicePrice(): number {
      return getServicePriceForDate(
        this.service,
        this.appointmentsSlotsSelected[0]?.visit_start_time ??
          this.visitStartTime
      );
    },
    getSelectedSlotsTotalPrice(): number {
      return this.appointmentsSlotsSelected.reduce(
        (total, slot) =>
          total + getServicePriceForDate(this.service, slot.visit_start_time),
        0
      );
    },
    getUserNameWithDegree(): string {
      if (this.anyAvailableUser) {
        return $gettext('Kdokoliv dostupný');
      }
      return this.user?.degree_full_name ?? '';
    },
    getVisitMultipleAppointmentsEnabled(): boolean {
      return this.service?.max_reservations_in_batch !== 1;
    },
    getUrlDomainWithSlug(): string {
      const slug = this.company?.reservation_slug;
      const domain = slug ? buildSubdomainUrl(slug) : getBaseDomain();
      return `https://${domain}`;
    },
  },
  actions: {
    setReservationSymptomsBirthNumber(birthNumber: string) {
      this.reservationSymptomsParams.patientsBirthNumber = birthNumber;
    },
    setReservationSymptomsDiagnosisId(
      diagnosisId: string,
      additionalOption?: string
    ) {
      this.reservationSymptomsParams.patientDiagnosisId = diagnosisId;
      if (additionalOption)
        this.reservationSymptomsParams.additionalOption = additionalOption;
    },
    addAppointmentSlot(newSlot: CreateVisitReservationTimeSlotSchema) {
      this.appointmentsSlotsSelected.push(newSlot);
      // sort; the first appointment is the earliest one
      this.appointmentsSlotsSelected.sort(
        (a, b) =>
          new Date(a.visit_start_time).getTime() -
          new Date(b.visit_start_time).getTime()
      );
    },
    setReservationFormFromPatientUser() {
      const userStore = useUser();
      let parsedPhone = null;

      if (userStore.patientUser?.phone) {
        parsedPhone = useParsePhoneNumberByPrefix(userStore.patientUser.phone);
      }

      this.formNewReservation = {
        firstName: userStore.patientUser?.first_name || '',
        lastName: userStore.patientUser?.last_name || '',
        email: userStore.patientUser?.email || '',
        countryCode: parsedPhone?.countryPrefix || '',
        phone: parsedPhone?.phoneNumber || '',
        note: '',
        file: null,
        gdprConsent: false,
        receiveNewsletterConsent: false,
        vopConsent: false,
        paymentType: null,
      };
    },

    setSubstituteFormFromPatientUser() {
      const userStore = useUser();
      let parsedPhone = null;
      if (userStore.patientUser?.phone) {
        parsedPhone = useParsePhoneNumberByPrefix(userStore.patientUser.phone);
      }
      this.formNewSubstituteReservation = {
        firstName: userStore.patientUser?.first_name || '',
        lastName: userStore.patientUser?.last_name || '',
        email: userStore.patientUser?.email || '',
        countryCode: parsedPhone?.countryPrefix || '',
        phone: parsedPhone?.phoneNumber || '',
        note: '',
        file: null,
        gdprConsent: false,
        receiveNewsletterConsent: false,
        vopConsent: false,
      };
    },

    setService(service: ReservationServiceSchema) {
      this.service = service;
    },

    setUser(user?: ReservationUserSchema) {
      this.user = user ?? null;
    },

    setCompanyId(companyId: string) {
      this.companyId = companyId;
    },

    setPatientDetails(patientDetails: PatientDetails) {
      this.patientData = patientDetails;
    },

    setVisitStartTime(visitDate: Date) {
      this.visitStartTime = visitDate;
    },

    async createReservation() {
      if (
        !this.service ||
        !this.user ||
        !this.appointmentsSlotsSelected.length ||
        !this.companyId ||
        !this.patientData
      ) {
        Notify.create({
          message: $gettext('Některé z povinných údajů chybí'),
          color: 'negative',
          icon: 'fa-solid fa-circle-exclamation',
          timeout: 1000,
        });
        return;
      }

      const requestBody: CreateVisitReservationSchema = {
        company_id: this.getCompanyId,
        service_id: this.getServiceId,
        location_id: this.location?.id,
        time_slots: this.appointmentsSlotsSelected,
        patient_email: this.patientData.patientEmail,
        patient_first_name: this.patientData.patientFirstName,
        patient_last_name: this.patientData.patientLastName,
        patient_phone: this.patientData.patientPhone,
        description: this.patientData.patientNote,
        gdpr_consent: this.patientData.gdprConsent,
        receive_newsletter_consent: this.patientData.receiveNewsletterConsent,
        vop_consent: this.patientData.vopConsent,
        require_payment_before: this.paymentBefore ?? false,
        diagnostic_answer_additional_option: this.reservationSymptomsParams
          .additionalOption.length
          ? this.reservationSymptomsParams.additionalOption
          : null,
        pay_with_ticket: this.payWithTicket,
        patient_language: currentLanguage.value as 'cs' | 'en' | 'sk' | 'pl',
      };

      if (this.reservationSymptomsParams.patientDiagnosisId) {
        requestBody.diagnostic_id =
          this.reservationSymptomsParams.patientDiagnosisId;
      }

      if (this.reservationSymptomsParams.patientsBirthNumber) {
        requestBody.patient_nin =
          this.reservationSymptomsParams.patientsBirthNumber;
      }

      await ReservationsApi.reservationsCreateVisitReservationPost({
        requestBody,
      })
        .then((r): void => {
          this.reservation = r;
        })
        .catchApiErrors((errors) => {
          this.reservation = null;
          this.errors = errors;

          const errorMessagesList: string[] = [];

          if (errors) {
            Object.keys(errors).forEach((key) => {
              errorMessagesList.push(
                ERROR_MESSAGES[key as keyof typeof ERROR_MESSAGES] ||
                  ERROR_MESSAGES.default
              );
            });
          }

          Object.values(errors).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
          });

          for (const message of errorMessagesList) {
            Notify.create({
              message: $gettext(message),
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
          }
        });
    },
    async createWaitingPatientReservation(): Promise<boolean> {
      if (!this.service || !this.user || !this.companyId || !this.patientData) {
        Notify.create({
          message: $gettext('Některé z povinných údajů chybí'),
          color: 'negative',
          icon: 'fa-solid fa-circle-exclamation',
          timeout: 1000,
        });
        return false;
      }

      const data: CreateWaitingPatientReservationSchema = {
        company_id: this.getCompanyId,
        user_id: this.getUserId,
        service_id: this.getServiceId,
        patient_email: this.patientData.patientEmail,
        patient_first_name: this.patientData.patientFirstName,
        patient_last_name: this.patientData.patientLastName,
        patient_phone: this.patientData.patientPhone,
        description: this.patientData.patientNote,
        gdpr_consent: this.patientData.gdprConsent,
        receive_newsletter_consent: this.patientData.receiveNewsletterConsent,
        vop_consent: this.patientData.vopConsent,
        patient_language: currentLanguage.value as 'cs' | 'en' | 'sk' | 'pl',
      };

      if (this.reservationSymptomsParams.patientsBirthNumber) {
        data.patient_nin = this.reservationSymptomsParams.patientsBirthNumber;
      }

      const formData: {
        file?: File | null;
        data: CreateWaitingPatientReservationSchema;
      } = {
        data,
      };
      if (this.patientData.patientFileInReservation) {
        formData.file = this.patientData.patientFileInReservation;
      }

      let success = false;
      await ReservationsApi.reservationsCreateWaitingPatientReservationPost({
        formData,
      })
        .then((): void => {
          success = true;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;

          Object.values(errors).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
          });
        });
      return success;
    },
    async uploadFileToReservation() {
      if (this.patientData?.patientFileInReservation) {
        // for MVP purpose the method still resolves even if the file upload fails
        try {
          await ReservationsApi.reservationsCreateVisitReservationFormPost({
            visitId: this.getReservationId,
            formData: { file: this.patientData.patientFileInReservation },
          });
        } catch {
          this.errors = {
            file_upload: [$gettext('Přiložený soubor se nepodařilo nahrát')],
          };
        }
      }
    },
    async fetchCompany(companyId: string) {
      // prevent multiple calls from different components at the same page
      if (_pendingFetchCompany) return _pendingFetchCompany;
      if (this.isFetching) return;

      this.isFetching = true;
      _pendingFetchCompany = ReservationsApi.reservationsGetCompanyGet({
        companyId: companyId,
      })
        .then((r) => {
          this.company = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst data'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        })
        .finally(() => {
          this.isFetching = false;
          _pendingFetchCompany = null;
        });
      return _pendingFetchCompany;
    },
    async fetchCompanyBySlug(slug: string) {
      await ReservationsApi.reservationsGetCompanyBasedOnSlugGet({ slug: slug })
        .then((r) => {
          this.company = r;
        })
        .catchApiErrors(async (errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst ordinaci na této adrese'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 2000,
          });
          await this.router.push({ name: 'error-not-found' });
        });
    },
    async fetchDiagnosticQuestionare(
      companyId: string,
      birthNumber: string,
      newDiagnostic: boolean = false
    ) {
      await ReservationsApi.reservationsGetDiagnosticAnswersGet({
        companyId: companyId,
        birthNumber: birthNumber,
        newDiagnostic: newDiagnostic,
      })
        .then((r) => {
          this.diagnosticQuestionareOptions = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Object.values(errors).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 1000,
            });
          });
        });
    },
    async fetchCompanyUsers(
      serviceId?: string | null,
      locationId?: string | null
    ) {
      await ReservationsApi.reservationsGetCompanyUsersGet({
        companyId: this.getCompanyId,
        serviceId: serviceId,
        locationId: locationId,
      })
        .then((r) => {
          this.companyUsers = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;

          // Handle specific unauthorized reservation error message
          if (
            errors.detail &&
            errors.detail[0] &&
            errors.detail[0].includes(
              'For online reservations you must be logged in to the client portal'
            )
          ) {
            Notify.create({
              message: errors.detail[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
            return;
          }

          Notify.create({
            message: $gettext('Nepodařilo se načíst dostupné fyzioterapeuty'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async fetchCompanyServiceGroups(
      locationId?: string,
      forWaitingPatients?: boolean
    ) {
      await ReservationsApi.reservationsGetCompanyServiceGroupsGet({
        companyId: this.getCompanyId,
        locationId: locationId,
        forWaitingPatients: forWaitingPatients,
      })
        .then((r) => {
          this.companyServiceGroups = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;

          // Handle specific unauthorized reservation error message
          if (
            errors.detail &&
            errors.detail[0] &&
            errors.detail[0].includes(
              'For online reservations you must be logged in to the client portal'
            )
          ) {
            Notify.create({
              message: errors.detail[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
            return;
          }

          Notify.create({
            message: $gettext('Nepodařilo se načíst skupiny služeb'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async fetchPublicServiceGroups(companyId: string) {
      await PublicApi.publicGetPublicCompanyServiceGroupsGet({
        companyId: companyId,
      })
        .then((r) => {
          this.companyServiceGroups = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst skupiny služeb'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async handleFetchCompanyUsers(
      serviceId: string | null | undefined,
      locationId?: string | null
    ) {
      if (serviceId) {
        await this.fetchCompanyUsers(serviceId, locationId);
      }
      return this.$state.companyUsers.length
        ? this.$state.companyUsers[0].name_of_clinic ?? ''
        : '';
    },
    async fetchCompanyServices(
      serviceGroupId?: string,
      locationId?: string,
      forWaitingPatients?: boolean
    ) {
      await ReservationsApi.reservationsGetCompanyServicesGet({
        companyId: this.getCompanyId,
        serviceGroupId: serviceGroupId,
        locationId: locationId,
        forWaitingPatients: forWaitingPatients,
      })
        .then((r) => {
          this.companyServices = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;

          // Handle specific unauthorized reservation error message
          if (
            errors.detail &&
            errors.detail[0] &&
            errors.detail[0].includes(
              'For online reservations you must be logged in to the client portal'
            )
          ) {
            Notify.create({
              message: errors.detail[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
            return;
          }

          Notify.create({
            message: $gettext('Nepodařilo se načíst služby'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async fetchPublicServices(companyId: string) {
      await PublicApi.publicGetPublicCompanyServicesGet({
        companyId: companyId,
      })
        .then((r) => {
          this.companyServices = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst služby'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async fetchPublicEmployees(companyId: string) {
      await PublicApi.publicGetPublicCompanyEmployeesGet({
        companyId: companyId,
      })
        .then((r) => {
          this.companyUsers = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst zaměstnance'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async checkTicketPayment() {
      await ReservationsApi.reservationsCheckPatientTicketPaymentPost({
        requestBody: {
          serviceId: this.getServiceId,
          startTimes: this.appointmentsSlotsSelected.map(
            (slot) => slot.visit_start_time
          ),
        },
      })
        .then((r) => {
          this.canPayWithTicket = r;
        })
        .catch((err) => {
          console.error(err);
        });
    },
    // fetch available time slots on the selected day
    async fetchCompanyTimeSlots() {
      this.slotsLoading = true;

      type RequestParams = {
        companyId: string;
        serviceId: string;
        locationId?: string | null;
        date: string;
        userIds?: Array<string>;
        patientNin?: string | null;
        diagnosticId?: string | null;
        visitId?: string | null;
        firstVisitInBatchDateTime?: string | null;
      };

      const { patientDiagnosisId } = this.reservationSymptomsParams;

      /* presence of patientNin means user is ordering so-called Extra service, which
       * requires different set of parameters.
       */
      const requestParams: RequestParams = {
        companyId: this.getCompanyId,
        serviceId: this.getServiceId,
        locationId: this.location?.id,
        date: this.getVisitStartTime.toISOString().substring(0, 10),
        ...(!this.getReservationId &&
          !this.getPatientsNin &&
          this.getUserIds && { userIds: this.getUserIds }),
        ...(this.getPatientsNin && { patientNin: this.getPatientsNin }),
        ...(patientDiagnosisId && { diagnosticId: patientDiagnosisId }),
        ...(!this.getPatientsNin &&
          this.getVisitId && { visitId: this.getVisitId }),
        firstVisitInBatchDateTime:
          this.appointmentsSlotsSelected[0]?.visit_start_time || null,
      };

      await ReservationsApi.reservationsGetCompanyTimeSlotsGet(requestParams)
        .then((r) => {
          this.companyTimeSlots = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;

          // Handle specific unauthorized reservation error message
          if (
            errors.detail &&
            errors.detail[0] &&
            errors.detail[0].includes(
              'For online reservations you must be logged in to the client portal'
            )
          ) {
            Notify.create({
              message: errors.detail[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
            return;
          }

          Notify.create({
            message: $gettext('Nepodařilo se načíst volné termíny'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });

      this.slotsLoading = false;
    },

    // fetch available calendar days
    async fetchCompanyAvailableDays(date: Date) {
      type RequestParams = {
        companyId: string;
        serviceId: string;
        locationId?: string | null;
        date: string;
        userIds?: Array<string>;
        patientNin?: string | null;
        diagnosticId?: string | null;
        visitId?: string | null;
        firstVisitInBatchDateTime?: string | null;
      };

      const { patientDiagnosisId } = this.reservationSymptomsParams;

      /* presence of patientNin means user is ordering so-called Extra service, which
       * requires different set of parameters.
       */
      const requestParams: RequestParams = {
        companyId: this.getCompanyId,
        serviceId: this.getServiceId,
        locationId: this.location?.id,
        date: date?.toISOString().substring(0, 10),
        ...(!this.getReservationId &&
          !this.getPatientsNin &&
          this.getUserIds && { userIds: this.getUserIds }),
        ...(this.getPatientsNin && { patientNin: this.getPatientsNin }),
        ...(patientDiagnosisId && { diagnosticId: patientDiagnosisId }),
        ...(!this.getPatientsNin &&
          this.getVisitId && { visitId: this.getVisitId }),
        firstVisitInBatchDateTime:
          this.appointmentsSlotsSelected[0]?.visit_start_time || null,
      };

      await ReservationsApi.reservationsGetCompanyTimeSlotsOverviewGet(
        requestParams
      )
        .then((r) => {
          this.companyAvailableDays = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst volné termíny'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async GetVisitReservation(visitId: string) {
      const requestParams = {
        visitId: visitId,
      };
      await ReservationsApi.reservationsGetVisitReservationGet(requestParams)
        .then((r) => {
          this.reservation = r;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Návštěva nenalezena'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async PatchVisitReservation(visitId: string, visitStartTime: string) {
      this.errors = {};
      const requestParams = {
        visit_start_time: visitStartTime,
      };
      await ReservationsApi.reservationsUpdateVisitReservationPatch({
        visitId: visitId,
        requestBody: requestParams,
      })
        .then((r) => {
          this.reservation = r;
        })
        .catchApiErrors((err) => {
          this.errors = err;
          Object.values(err).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 1000,
            });
          });
        });
    },
    async CancelVisitReservation() {
      if (!this.reservation) {
        Notify.create({
          message: $gettext('Rezervaci se nepodařilo zrušit'),
          color: 'negative',
          icon: 'fa-solid fa-circle-exclamation',
          timeout: 1000,
        });
        return;
      }
      const requestParams = {
        visitId: this.getReservationId,
      };
      await ReservationsApi.reservationsDeleteVisitReservationDelete(
        requestParams
      )
        .then(() => {
          this.errors = {};
          Notify.create({
            message: $gettext('Rezervace byla zrušena'),
            color: 'positive',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Rezervaci se nepodařilo zrušit'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 1000,
          });
        });
    },
    async fetchCompanyDiagnostic() {
      await DiagnosticsApi.diagnosticsGetCompanyDiagnosticsGet()
        .then((response) => {
          this.errors = {};
          this.diagnosesInCompany = response;
        })
        .catchApiErrors((errors) => {
          this.errors = errors;
          Notify.create({
            message: $gettext('Nepodařilo se načíst seznam diagnóz'),
            color: 'negative',
            icon: 'fa-solid fa-circle-exclamation',
            timeout: 2000,
          });
        });
    },

    async createSubstituteReservation(patientData: PatientDetails) {
      if (!this.selectedSubstituteSlot || !this.companyId) {
        Notify.create({
          message: $gettext('Některé z povinných údajů chybí'),
          color: 'negative',
          icon: 'fa-solid fa-circle-exclamation',
          timeout: 1000,
        });
        return;
      }

      const groupVisitId = this.selectedSubstituteSlot.group_visit_id;
      if (!groupVisitId) {
        Notify.create({
          message: $gettext('Nepodařilo se vytvořit registraci jako náhradník'),
          color: 'negative',
          icon: 'fa-solid fa-circle-exclamation',
          timeout: 1000,
        });
        return;
      }

      const requestBody: CreateSubstituteReservationSchema = {
        company_id: this.getCompanyId,
        group_visit_id: groupVisitId,
        patient_email: patientData.patientEmail,
        patient_first_name: patientData.patientFirstName,
        patient_last_name: patientData.patientLastName,
        patient_phone: patientData.patientPhone,
        patient_nin: null,
        description: patientData.patientNote,
        gdpr_consent: patientData.gdprConsent,
        receive_newsletter_consent: patientData.receiveNewsletterConsent,
        vop_consent: patientData.vopConsent,
      };

      await ReservationsApi.reservationsCreateSubstituteReservationPost({
        requestBody,
      })
        .then((r): void => {
          this.substituteReservation = r;
          Notify.create({
            message: $gettext(
              'Úspěšně jste byli zaregistrováni jako náhradník'
            ),
            color: 'positive',
            icon: 'fa-solid fa-circle-check',
            timeout: 3000,
          });
        })
        .catchApiErrors((errors) => {
          this.substituteReservation = null;
          this.errors = errors;

          Object.values(errors).forEach((e) => {
            Notify.create({
              message: e[0],
              color: 'negative',
              icon: 'fa-solid fa-circle-exclamation',
              timeout: 5000,
            });
          });
        });
    },
  },
});
